import { useState } from "react";
import { ChevronDown, ChevronUp, Scale, Loader2 } from "lucide-react";
import { authFetch } from "@/lib/api";
import { toast } from "sonner";
import { API_BASE } from "./constants";

// Dose par litre de bouillie (mL/L…) : il faut le volume de bouillie à l'hectare ; en litres, aussi la densité
export const CONC_UNITS = ["mL/L", "mL/hL", "L/hL", "cL/L", "g/L", "g/hL", "kg/hL"];
export const isConcUnit = (u) => CONC_UNITS.includes(u);
export const isVolumeUnit = (u) => ["L/ha", "mL/ha", "hL/ha", "m3/ha", "m³/ha", "mL/L", "mL/hL", "L/hL", "cL/L"].includes(u);

// Éléments du bilan (Anthony, 26/10/2026) : tous affichés, même à 0, oligo-éléments un par un
export const ELEMENTS = [
  { code: "N", label: "N" }, { code: "P2O5", label: "P₂O₅" }, { code: "K2O", label: "K₂O" },
  { code: "MgO", label: "MgO" }, { code: "CaO", label: "CaO" }, { code: "SO3", label: "SO₃" },
  { code: "Fe", label: "Fe" }, { code: "Mn", label: "Mn" }, { code: "B", label: "B" },
  { code: "Zn", label: "Zn" }, { code: "Cu", label: "Cu" }, { code: "Mo", label: "Mo" },
];
const LABEL = Object.fromEntries(ELEMENTS.map((e) => [e.code, e.label]));

/** 60 → « 60 kg », 1.25 → « 1,3 kg », 0.075 → « 75 g » : les oligo-éléments se lisent en grammes. */
export const fmtKg = (v) => {
  if (!v) return "0";
  if (v < 1) return `${(v * 1000).toLocaleString("fr-FR", { maximumFractionDigits: v < 0.01 ? 1 : 0 })} g`;
  return `${v.toLocaleString("fr-FR", { maximumFractionDigits: v < 1000 ? 1 : 0 })} kg`;
};

/** Complète un produit non compté (teneurs lues sur l'emballage, ou densité pour une dose en litres) :
 * enregistré dans « mes produits », ses apports passés et futurs sont comptés. */
export function CompleterProduit({ info, onDone }) {
  const [open, setOpen] = useState(false);
  const [teneurs, setTeneurs] = useState({});
  const [densite, setDensite] = useState("");
  const [bouillie, setBouillie] = useState("");
  const [saving, setSaving] = useState(false);
  if (!info?.completer || !info.product_name) return null;
  const save = async () => {
    setSaving(true);
    try {
      if (info.completer === "bouillie") {
        // Le volume de bouillie appartient à l'apport, pas au produit
        const res = await authFetch(`${API_BASE}/carnet/apports/${info.type}/${info.id}/bouillie`, {
          method: "PUT", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ volume_bouillie_l_ha: parseFloat(String(bouillie).replace(",", ".")) }),
        });
        const d = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Enregistrement impossible");
        toast.success("Volume de bouillie enregistré");
        setOpen(false); onDone?.(); setSaving(false);
        return;
      }
      const res = await authFetch(`${API_BASE}/carnet/mes-produits`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: info.product_name, category: info.category || null, unit: info.unit || null,
          teneurs: info.completer === "teneurs" ? teneurs : null, densite: densite ? parseFloat(String(densite).replace(",", ".")) : null }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof d.detail === "string" ? d.detail : "Enregistrement impossible");
      toast.success("Produit complété : ses apports sont comptés");
      setOpen(false);
      onDone?.();
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };
  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className="text-[10px] font-semibold underline" style={{ color: "#2D6A4F" }} data-testid="completer-produit">
        {info.completer === "densite" ? "Indiquer la densité du produit" : info.completer === "bouillie" ? "Indiquer le volume de bouillie" : "Compléter les teneurs du produit"}
      </button>
    );
  }
  const needDensite = info.completer === "densite" || (info.completer === "teneurs" && isVolumeUnit(info.unit));
  return (
    <div className="mt-1.5 space-y-2" data-testid="completer-form">
      {info.completer === "teneurs" && <TeneursFields teneurs={teneurs} onChange={setTeneurs} />}
      {needDensite && <DensiteField value={densite} onChange={setDensite} />}
      {info.completer === "bouillie" && <BouillieField value={bouillie} onChange={setBouillie} unit={info.unit} />}
      <div className="flex gap-2">
        <button type="button" onClick={save} disabled={saving} className="h-7 px-3 rounded-md text-[11px] font-semibold text-white" style={{ background: "#2D6A4F" }} data-testid="completer-ok">
          {saving && <Loader2 className="inline w-3 h-3 mr-1 animate-spin" />}Enregistrer dans mes produits
        </button>
        <button type="button" onClick={() => setOpen(false)} className="h-7 px-2 text-[11px]" style={{ color: "#7A7A7A" }}>Annuler</button>
      </div>
    </div>
  );
}

/** Volume de bouillie à l'hectare, pour une dose donnée par litre de bouillie (mL/L, g/hL…). */
export function BouillieField({ value, onChange, unit }) {
  return (
    <label className="block" data-testid="bouillie-field">
      <span className="text-[10px] font-semibold" style={{ color: "#4A4A4A" }}>Volume de bouillie (L/ha)</span>
      <input inputMode="decimal" value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder="Ex. 400"
        className="w-full h-8 px-2 mt-0.5 rounded-md border border-black/10 bg-white text-sm tabular-nums" data-testid="bouillie-input" />
      <span className="text-[10px]" style={{ color: "#7A7A7A" }}>Dose en {unit || "concentration"} : le volume de bouillie donne la quantité apportée à l'hectare.</span>
    </label>
  );
}

/** Densité d'un produit liquide (kg/L), pour compter une dose en litres. */
export function DensiteField({ value, onChange }) {
  return (
    <label className="block" data-testid="densite-field">
      <span className="text-[10px] font-semibold" style={{ color: "#4A4A4A" }}>Densité du produit (kg/L)</span>
      <input inputMode="decimal" value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder="Ex. 1,25 (sur l'emballage ou la fiche technique)"
        className="w-full h-8 px-2 mt-0.5 rounded-md border border-black/10 bg-white text-sm tabular-nums" data-testid="densite-input" />
      <span className="text-[10px]" style={{ color: "#7A7A7A" }}>Nécessaire pour compter une dose en litres.</span>
    </label>
  );
}

/** Unités apportées par un apport : « N 60 kg · P₂O₅ 55 kg · Fe 1 kg » (éléments à 0 omis). */
export function UnitesApport({ info, onCompleted }) {
  if (!info) return null;
  if (!info.units) return (
    <div>
      <p className="text-[10px] italic" style={{ color: "#9A6A12" }}>Non compté dans le bilan : {info.raison}</p>
      <CompleterProduit info={info} onDone={onCompleted} />
    </div>
  );
  const parts = ELEMENTS.filter((e) => info.units[e.code]).map((e) => `${e.label} ${fmtKg(info.units[e.code])}`);
  if (!parts.length) return null;
  return <p className="text-[10px] tabular-nums" style={{ color: "#2D6A4F" }} data-testid="fert-unites">{parts.join(" · ")} /ha</p>;
}

export default function BilanFertilisation({ bilan, depuis, onDepuis, loading, onCompleted }) {
  const [open, setOpen] = useState(true);
  const [showNonComptes, setShowNonComptes] = useState(false);
  const els = Object.fromEntries((bilan?.elements || []).map((e) => [e.code, e.total_kg_ha]));
  const nonComptes = bilan?.non_comptes || [];

  return (
    <div className="mx-3 mt-3 rounded-xl border border-black/5 p-3 space-y-2" style={{ background: "#FAFAF7" }} data-testid="fert-bilan">
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center gap-1.5 text-left">
        <Scale className="w-3.5 h-3.5" style={{ color: "#8B5E3C" }} />
        <span className="flex-1 text-[10px] font-bold uppercase tracking-wider" style={{ color: "#8B5E3C" }}>Bilan des apports (par ha)</span>
        {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
      </button>
      {open && (
        <>
          <label className="flex items-center gap-2 text-[11px]" style={{ color: "#4A4A4A" }}>
            Depuis le
            <input type="date" value={depuis} onChange={(e) => e.target.value && onDepuis(e.target.value)}
              className="h-7 px-2 rounded-md border border-black/10 bg-white text-[11px]" data-testid="fert-bilan-depuis" />
            {loading && <span className="text-[10px]" style={{ color: "#AAAAAA" }}>…</span>}
          </label>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5">
            {ELEMENTS.map((e) => {
              const v = els[e.code] || 0;
              return (
                <div key={e.code} className="rounded-lg px-2 py-1.5 bg-white border border-black/5" data-testid={`fert-bilan-${e.code}`}>
                  <p className="text-[10px] font-semibold" style={{ color: "#4A4A4A" }}>{e.label}</p>
                  <p className="text-xs font-bold tabular-nums" style={{ color: v ? "#1A1A1A" : "#C2410C" }}>{fmtKg(v)}</p>
                  {!v && <p className="text-[9px]" style={{ color: "#C2410C" }}>non apporté</p>}
                </div>
              );
            })}
          </div>
          <p className="text-[10px]" style={{ color: "#9A9A9A" }}>
            Fertilisation et biostimulants · {bilan?.apports_comptes ?? 0} apport(s) compté(s). Pas encore d'objectif : il viendra de la fiche ferti.
          </p>
          {nonComptes.length > 0 && (
            <div>
              <button type="button" onClick={() => setShowNonComptes((s) => !s)} className="text-[10px] font-semibold underline" style={{ color: "#9A6A12" }} data-testid="fert-bilan-non-comptes">
                {nonComptes.length} apport(s) non compté(s)
              </button>
              {showNonComptes && (
                <ul className="mt-1 space-y-0.5">
                  {nonComptes.map((n) => (
                    <li key={n.id} className="text-[10px]" style={{ color: "#4A4A4A" }}>
                      {n.date} · {n.product_name} — <span style={{ color: "#9A6A12" }}>{n.raison}</span>
                      <CompleterProduit info={n} onDone={onCompleted} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/** Cases des teneurs (% du produit brut), lues sur l'emballage ; une case vide n'est pas comptée. */
export function TeneursFields({ teneurs, onChange }) {
  const [oligoOpen, setOligoOpen] = useState(Object.keys(teneurs.oligo || {}).length > 0);
  const setMaj = (k, v) => onChange({ ...teneurs, [k]: v });
  const setOli = (k, v) => onChange({ ...teneurs, oligo: { ...(teneurs.oligo || {}), [k]: v } });
  const cell = (code, value, set) => (
    <label key={code} className="block">
      <span className="text-[10px] font-semibold" style={{ color: "#4A4A4A" }}>{LABEL[code]} %</span>
      <input inputMode="decimal" value={value ?? ""} onChange={(e) => set(code, e.target.value)}
        className="w-full h-8 px-2 mt-0.5 rounded-md border border-black/10 bg-white text-sm tabular-nums" data-testid={`teneur-${code}`} />
    </label>
  );
  return (
    <div className="rounded-lg border border-black/10 p-3 space-y-2" data-testid="fert-teneurs">
      <p className="text-[11px] uppercase tracking-wider font-medium" style={{ color: "#4A4A4A" }}>Teneurs du produit</p>
      <p className="text-[10px]" style={{ color: "#7A7A7A" }}>En % du produit, lues sur l'emballage. Laissez vide ce qui n'est pas indiqué.</p>
      <div className="grid grid-cols-3 gap-2">
        {["N", "P2O5", "K2O", "MgO", "CaO", "SO3"].map((c) => cell(c, teneurs[c], setMaj))}
      </div>
      <button type="button" onClick={() => setOligoOpen((o) => !o)} className="text-[10px] font-semibold underline" style={{ color: "#2D6A4F" }}>
        {oligoOpen ? "Masquer les oligo-éléments" : "Ajouter les oligo-éléments (Fe, Mn, B, Zn, Cu, Mo)"}
      </button>
      {oligoOpen && (
        <div className="grid grid-cols-3 gap-2">
          {["Fe", "Mn", "B", "Zn", "Cu", "Mo"].map((c) => cell(c, (teneurs.oligo || {})[c], setOli))}
        </div>
      )}
    </div>
  );
}

/** « Engrais 15-12-24 » → { N: "15", P2O5: "12", K2O: "24" } ; rien si le nom n'a pas de formule. */
export function teneursFromName(name) {
  const m = String(name || "").match(/(\d+(?:[.,]\d+)?)\s*-\s*(\d+(?:[.,]\d+)?)\s*-\s*(\d+(?:[.,]\d+)?)/);
  return m ? { N: m[1], P2O5: m[2], K2O: m[3] } : null;
}
